import { useState } from 'react';
import { usePathname } from '@/shared/lib/navigation';
import { Menu, X } from 'lucide-react';

import { useAuth } from '@/features/auth';
import { Button } from '@/shared/ui/button';
import { activeNavItem, visibleGroups } from '@/widgets/sidebar/model/nav-config';
import { NavLink } from '@/widgets/sidebar/ui/sidebar';

/**
 * The drawer, rendering the same `NAV_GROUPS` the desktop rail does.
 *
 * It used to carry its own flat array — 20 destinations against the rail's 31,
 * with the whole catalog (tools, brands, agencies, their categories and tags,
 * and experts) reachable only on a wide screen. It also matched the active item
 * with `pathname === item.href`, so a sub-route like `/dashboard/tools/tags`
 * highlighted nothing; it now uses the same longest-prefix match as the rail.
 *
 * The group headings come with the groups. At 20 entries a flat list was
 * arguable; at 31 it is not.
 */
export function MobileSidebar() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const { user } = useAuth();

  const groups = visibleGroups(user);
  const active = activeNavItem(pathname, groups);

  return (
    <div className="lg:hidden">
      <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} aria-label="Toggle menu">
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-xs"
          onClick={() => setOpen(false)}
        >
          <div
            className="fixed left-0 top-0 h-full w-64 overflow-y-auto border-r bg-background p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Menu</h2>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav aria-label="Menu" className="flex flex-col gap-1">
              {groups.map((group, index) => (
                <div key={group.label ?? `group-${index}`} className="flex flex-col gap-1">
                  {index > 0 && <div className="my-2 border-t border-border" />}
                  {group.label && (
                    <div className="px-3 py-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </p>
                    </div>
                  )}
                  {group.items.map((item) => (
                    <NavLink
                      key={item.href}
                      item={item}
                      isActive={active?.href === item.href}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
